import { useMemo, useState } from 'react';
import { ArrowRight, Gamepad2, ShieldCheck, Users, Wifi } from 'lucide-react';
import { TokenSprite } from './TokenSprite.jsx';
import { TOKEN_LABELS } from '../utils/board.js';

const TOKENS = Object.keys(TOKEN_LABELS);

export function Lobby({ connected, createRoom, joinRoom, initialRoomCode = '' }) {
  const [mode, setMode] = useState(initialRoomCode ? 'join' : 'create');
  const [name, setName] = useState('');
  const [roomCode, setRoomCode] = useState(initialRoomCode);
  const [token, setToken] = useState('rocket');
  const [busy, setBusy] = useState(false);
  const [spectator, setSpectator] = useState(false);
  const title = useMemo(() => (mode === 'create' ? 'Создать комнату' : 'Войти по коду'), [mode]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      if (mode === 'create') await createRoom({ name, token, spectator });
      else await joinRoom({ roomCode, name, token });
    } catch {
      // Ошибка уже показана общей системой уведомлений.
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="lobby-shell">
      <section className="lobby-story" aria-labelledby="lobby-title">
        <div className="brand-lockup brand-lockup--large">
          <span className="brand-mark" aria-hidden="true"><i /><i /><i /></span>
          <span>Капиталия</span>
        </div>
        <h1 id="lobby-title">Город строится<br />вашими решениями</h1>
        <p>
          Оригинальная экономическая настольная игра для друзей в одной Wi‑Fi сети.
          Сервер считает каждый бросок, платёж и сделку.
        </p>
        <div className="lobby-features" aria-label="Возможности игры">
          <span><Users size={19} /> 2–4 игрока</span>
          <span><Wifi size={19} /> Синхронно</span>
          <span><ShieldCheck size={19} /> Честный сервер</span>
        </div>
        <div className="lobby-city" aria-hidden="true" />
      </section>

      <section className="lobby-card" aria-labelledby="entry-title">
        <div className="connection-line">
          <span className={connected ? 'status-dot status-dot--online' : 'status-dot'} />
          {connected ? 'Сервер доступен' : 'Подключаемся к серверу…'}
        </div>
        <div className="segmented-control" role="tablist" aria-label="Способ входа">
          <button type="button" role="tab" aria-selected={mode === 'create'} onClick={() => setMode('create')}>Создать</button>
          <button type="button" role="tab" aria-selected={mode === 'join'} onClick={() => setMode('join')}>Войти</button>
        </div>
        <form onSubmit={submit}>
          <header className="lobby-card__header">
            <Gamepad2 aria-hidden="true" />
            <div>
              <h2 id="entry-title">{title}</h2>
              <p>{mode === 'create' ? 'Получите короткий код для друзей' : 'Введите код с экрана хозяина'}</p>
            </div>
          </header>

          <label className="field">
            <span>Ваше имя</span>
            <input value={name} onChange={(event) => setName(event.target.value)} maxLength={20} placeholder="Например, Иван" autoComplete="nickname" required />
          </label>

          {mode === 'join' && (
            <label className="field">
              <span>Код комнаты</span>
              <input className="room-code-input" value={roomCode} onChange={(event) => setRoomCode(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4))} minLength={4} maxLength={4} placeholder="Q7K2" autoCapitalize="characters" required />
            </label>
          )}

          {mode === 'create' && <label className="bot-observer"><input type="checkbox" checked={spectator} onChange={(event) => setSpectator(event.target.checked)} /><span>Только боты — я наблюдаю<br /><small>2–4 бота, матч идёт автоматически</small></span></label>}

          <fieldset className="token-picker">
            <legend>Выберите фишку</legend>
            <div>
              {TOKENS.map((value) => (
                <button key={value} type="button" className={token === value ? 'is-selected' : ''} onClick={() => setToken(value)} aria-pressed={token === value} aria-label={TOKEN_LABELS[value]}>
                  <TokenSprite token={value} size="sm" />
                </button>
              ))}
            </div>
          </fieldset>

          <button className="button button--primary button--wide" type="submit" disabled={!connected || busy}>
            {busy ? 'Подождите…' : title}
            {!busy && <ArrowRight size={19} />}
          </button>
        </form>
        <p className="privacy-note">Никаких аккаунтов и внешних сервисов — игра остаётся в вашей локальной сети.</p>
      </section>
    </main>
  );
}
